import type {
  CloudProvider,
  FilterType,
  Instance,
  ProviderRegion,
} from "../components/table/types";
import {
  compareOperatingSystems,
  comparePaymentTypes,
  compareRegions,
  multipliers,
} from "./constants";

interface RawProviderResponse {
  provider: "AWS" | "GCP" | "Azure";
  data: {
    Data: {
      CloudProvider: string;
      Items: any[];
    };
  };
}

export function formatSimilarInstancesByProvider(
  response: RawProviderResponse[]
): { provider: string; items: Instance[] }[] {
  return response.map(({ provider, data }) => {
    const items = data?.Data?.Items || [];
    const formattedItems: Instance[] = items.map((item: any, i: number) => {
      // GPU fields
      const gpuMemoryInMB = item.GPUMemoryInMB ?? null;
      const gpuName = item.GPUName ?? "";
      const hasGpu = item.HasGPU ?? false;
      const gpuMemoryGB = gpuMemoryInMB ? gpuMemoryInMB / 1024 : "-";

      return {
        id: `${provider.toLowerCase()}-${item.InstanceType || i}`,
        instanceType: item.InstanceType || "-",
        instanceFamily:
          (item.InstanceType || "").split(".")[0]?.toUpperCase() ||
          (item.InstanceType || "").split("_")[0]?.toUpperCase() ||
          "-",
        vcpus: item.ProcessorVCPUCount ?? "-",
        memory: item.MemorySizeInMB ? item.MemorySizeInMB / 1024 : "-",
        cpuArchitecture:
          item.ProcessorArchitecture || item.cpuArchitecture || "x86_64",
        gpu: gpuMemoryGB,
        gpuName,
        hasGpu,
        aws: provider === "AWS" ? item.PricePerHour ?? "-" : "-",
        gcp: provider === "GCP" ? item.PricePerHour ?? "-" : "-",
        azure:
          provider === "Azure"
            ? item.PricePerHour ?? item.linuxPrice ?? "-"
            : "-",
        best: provider,
      };
    });

    return {
      provider: provider.toLowerCase(),
      items: formattedItems,
    };
  });
}

type ProviderType = "aws" | "gcp" | "azure";

export function compareInstances(
  awsInstances: Instance[],
  gcpInstances: Instance[],
  azureInstances: Instance[],
  providerType: ProviderType
): Instance[] {
  // Pick base provider array
  const baseInstances =
    providerType === "aws"
      ? awsInstances
      : providerType === "gcp"
        ? gcpInstances
        : azureInstances;

  // Helper: safely parse price
  const toNumber = (val: any) =>
    typeof val === "number" ? val : parseFloat(val) || null;

  // Helper: find lowest price provider
  const getBest = (
    aws?: number | null,
    gcp?: number | null,
    azure?: number | null
  ) => {
    const prices = [
      { name: "AWS", value: aws },
      { name: "GCP", value: gcp },
      { name: "Azure", value: azure },
    ].filter((p) => typeof p.value === "number" && !isNaN(p.value));

    if (prices.length === 0) return "-";
    return prices.reduce((prev, curr) =>
      curr.value! < prev.value! ? curr : prev
    ).name;
  };

  // Main comparison
  return baseInstances.map((base) => {
    // Find matches by vCPUs + memory
    const awsMatch = awsInstances.find(
      (i) => i.vcpus === base.vcpus && i.memory === base.memory
    );
    const gcpMatch = gcpInstances.find(
      (i) => i.vcpus === base.vcpus && i.memory === base.memory
    );
    const azureMatch = azureInstances.find(
      (i) => i.vcpus === base.vcpus && i.memory === base.memory
    );

    // Extract numeric prices
    const awsPrice = toNumber(awsMatch?.aws ?? null);
    const gcpPrice = toNumber(gcpMatch?.gcp ?? null);
    const azurePrice = toNumber(azureMatch?.azure ?? null);

    // Return the base instance enriched with comparison
    return {
      ...base,
      aws: awsPrice ?? "-",
      gcp: gcpPrice ?? "-",
      azure: azurePrice ?? "-",
      best: getBest(awsPrice, gcpPrice, azurePrice),
    };
  });
}

export function findSimilarInstances(
  instance: Instance,
  awsInstances: Instance[],
  gcpInstances: Instance[],
  azureInstances: Instance[],
  providerType: ProviderType
) {
  const { vcpus, memory } = instance;

  // Helper to check "similar" specs (exact match)
  const isSimilar = (item: Instance) =>
    item.vcpus === vcpus && item.memory === memory;

  // Helper to get best provider
  const getBest = (
    aws?: number | string | null,
    gcp?: number | string | null,
    azure?: number | string | null
  ) => {
    const prices = [
      { name: "AWS", value: aws },
      { name: "GCP", value: gcp },
      { name: "Azure", value: azure },
    ].filter((p) => typeof p.value === "number" && !isNaN(p.value));

    if (prices.length === 0) return "-";
    return prices.reduce((prev, curr) =>
      curr.value! < prev.value! ? curr : prev
    ).name;
  };

  const toNumber = (val: any) =>
    typeof val === "number" ? val : parseFloat(val) || null;

  // 🔹 Get filtered items per provider
  const awsMatches = awsInstances.filter(isSimilar);
  const gcpMatches = gcpInstances.filter(isSimilar);
  const azureMatches = azureInstances.filter(isSimilar);

  // 🔹 Format items with provider-specific info
  const formatItems = (items: Instance[], provider: string) =>
    items.map((item) => {
      const aws = provider === "aws" ? toNumber(item.aws) : "-";
      const gcp = provider === "gcp" ? toNumber(item.gcp) : "-";
      const azure = provider === "azure" ? toNumber(item.azure) : "-";

      return {
        id: `${provider}-${item.instanceType}`,
        instanceType: item.instanceType,
        instanceFamily: item.instanceFamily || "",
        vcpus: item.vcpus,
        memory: item.memory,
        cpuArchitecture: item.cpuArchitecture || "x86_64",
        gpu: item.gpu ?? "-",
        gpuName: item.gpuName ?? "",
        hasGpu: item.hasGpu ?? false,
        aws,
        gcp,
        azure,
        best: getBest(aws, gcp, azure),
      };
    });

  // 🔹 Final structured output
  return [
    { provider: "aws", items: formatItems(awsMatches, "aws") },
    { provider: "gcp", items: formatItems(gcpMatches, "gcp") },
    { provider: "azure", items: formatItems(azureMatches, "azure") },
  ];
}

export function getRegions(provider: CloudProvider): ProviderRegion[] {
  return compareRegions
    .map((region) => region[provider])
    .filter((r): r is ProviderRegion => !!r); // type guard removes nulls
}
export function getPaymentModal(provider: CloudProvider): ProviderRegion[] {
  return comparePaymentTypes
    .map((paymentModal) => paymentModal[provider])
    .filter((r): r is ProviderRegion => !!r); // type guard removes nulls
}
export function getOperatingSystems(provider: CloudProvider): ProviderRegion[] {
  return compareOperatingSystems
    .map((operatingSystem) => operatingSystem[provider])
    .filter((r): r is ProviderRegion => !!r); // type guard removes nulls
}
export const getProviderMapping = (
  providerType: CloudProvider,
  selectedValue: string,
  data: FilterType[]
): Record<CloudProvider, string | undefined> => {
  const matchedItem = data.find(
    (item) => item[providerType]?.value === selectedValue
  );

  return {
    aws: matchedItem?.aws?.value,
    gcp: matchedItem?.gcp?.value,
    azure: matchedItem?.azure?.value,
  };
};


export function convertPricesByUnit(
  data: any[],
  previousUnit: string,
  newUnit: string
) {
  const prevMultiplier = multipliers[previousUnit] ?? 1;
  const newMultiplier = multipliers[newUnit] ?? 1;

  // Conversion factor
  const factor = newMultiplier / prevMultiplier;

  return data.map((item) => ({
    ...item,
    aws:
      typeof item.aws === "number"
        ? +(item.aws * factor).toFixed(5)
        : item.aws,
    gcp:
      typeof item.gcp === "number"
        ? +(item.gcp * factor).toFixed(5)
        : item.gcp,
    azure:
      typeof item.azure === "number"
        ? +(item.azure * factor).toFixed(5)
        : item.azure,
  }));
}
export function getMinMax<T extends Record<string, any>>(
  data: T[],
  key: keyof T
): { min: number; max: number } {
  const nums = data
    .map((item) => Number(item[key]))
    .filter((n) => !isNaN(n));

  return {
    min: Math.min(...nums),
    max: Math.max(...nums),
  };
}
